import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { SEO, Breadcrumb } from '@/components/seo';
import { FAQSchema } from '@/components/seo/Schema';
import {
  ArrowRight,
  Loader2,
  ExternalLink,
  Cpu,
  Shield,
  DollarSign,
  Zap,
  Droplet,
  ChevronDown,
  BarChart3,
  FlaskConical,
  Info,
} from 'lucide-react';
import { buttonStyles } from '@/components/ui';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = import.meta.env.VITE_API_URL || 'https://lionfish-app-zwr6q.ondigitalocean.app/api/v1';

interface FAQItem {
  q: string;
  a: string;
}

interface ServiceData {
  id: string;
  slug: string;
  name: string;
  vendor: string;
  url: string | null;
  description: string | null;
  longDescription: string | null;
  faq: FAQItem[] | null;
  category: string | null;
  hasFreeplan: boolean;
  pricingInfo: string | null;
  logoUrl: string | null;
}

interface ModelData {
  slug: string;
  name: string;
  vendor: string;
  avgDetectionRate: number | null;
  isDefault: boolean;
  logoUrl: string | null;
}

interface GeneratorData {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  inputType: string | null;
  outputType: string | null;
}

interface WatermarkData {
  slug: string;
  name: string;
  developer: string | null;
  type: string | null;
  isRemovable: boolean;
}

interface DetectionStat {
  slug: string;
  name: string;
  total: number;
  correct: number;
  successRate: number;
  avgScore: number | null;
}

interface ModelDetectionStat {
  slug: string;
  name: string;
  vendor: string;
  total: number;
  correct: number;
  successRate: number;
  avgScore: number | null;
}

interface RelatedService {
  slug: string;
  name: string;
  vendor: string;
  category: string | null;
  hasFreeplan: boolean;
  logoUrl: string | null;
}

export function ServicePage() {
  const { slug } = useParams<{ slug: string }>();
  const [service, setService] = useState<ServiceData | null>(null);
  const [models, setModels] = useState<ModelData[]>([]);
  const [generators, setGenerators] = useState<GeneratorData[]>([]);
  const [watermarks, setWatermarks] = useState<WatermarkData[]>([]);
  const [detectionStatsByDetector, setDetectionStatsByDetector] = useState<DetectionStat[]>([]);
  const [detectionStatsByModel, setDetectionStatsByModel] = useState<ModelDetectionStat[]>([]);
  const [relatedServices, setRelatedServices] = useState<RelatedService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/pages/services/${slug}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to load service');
        }

        setService(data.service);
        setModels(data.models || []);
        setGenerators(data.generators || []);
        setWatermarks(data.watermarks || []);
        setDetectionStatsByDetector(data.detectionStatsByDetector || []);
        setDetectionStatsByModel(data.detectionStatsByModel || []);
        setRelatedServices(data.relatedServices || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (error || !service) {
    return <Navigate to="/" replace />;
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' },
    { label: service.name },
  ];

  const getCategoryLabel = (cat: string | null) => {
    switch (cat) {
      case 'image-generator': return 'Image Generator';
      case 'video-generator': return 'Video Generator';
      case 'chatbot': return 'AI Chatbot';
      default: return 'AI Service';
    }
  };

  // Compute aggregate stats
  const avgDetectionRate = detectionStatsByDetector.length > 0
    ? Math.round(detectionStatsByDetector.reduce((sum, d) => sum + d.successRate, 0) / detectionStatsByDetector.length)
    : null;

  return (
    <>
      <SEO
        title={`${service.name} AI Detection & Watermark Analysis - Benchmark Results | AI Detector Arena`}
        description={`How detectable are ${service.name} images? Detection rates, watermark analysis, and benchmark results across ${detectionStatsByDetector.length} AI detectors. Live benchmark data.`}
        canonical={`/services/${service.slug}`}
      />

      {/* FAQ Schema for rich snippets */}
      {service.faq && service.faq.length > 0 && (
        <FAQSchema
          questions={service.faq.map(item => ({
            question: item.q,
            answer: item.a,
          }))}
        />
      )}

      {/* Hero */}
      <section className="section bg-gradient-to-b from-blue-50 via-white to-white">
        <div className="container">
          <Breadcrumb items={breadcrumbItems} />

          <div className="max-w-4xl mx-auto text-center mt-8">
            {service.logoUrl && (
              <img
                src={service.logoUrl}
                alt={`${service.name} logo`}
                className="w-16 h-16 object-contain mx-auto mb-4"
              />
            )}

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {service.name} AI Detection & Watermark Analysis
            </h1>

            <p className="text-lg text-gray-600 mb-4 max-w-2xl mx-auto">
              {service.description}
            </p>

            {service.url && (
              <a
                href={service.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm mb-8"
              >
                Visit {service.name} <ExternalLink className="w-3 h-3" />
              </a>
            )}

            {/* Quick Stats */}
            <div className="flex justify-center gap-8 mt-6">
              <div className="text-center">
                <div className={`text-3xl font-bold ${
                  avgDetectionRate !== null && avgDetectionRate >= 70 ? 'text-red-500' :
                  avgDetectionRate !== null && avgDetectionRate >= 40 ? 'text-yellow-500' :
                  avgDetectionRate !== null ? 'text-green-500' : 'text-gray-400'
                }`}>
                  {avgDetectionRate !== null ? `${avgDetectionRate}%` : '—'}
                </div>
                <div className="text-sm text-gray-500">Avg Detection Rate</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-500">{watermarks.length}</div>
                <div className="text-sm text-gray-500">Watermark Types</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-500">{detectionStatsByDetector.length}</div>
                <div className="text-sm text-gray-500">Detectors Tested</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-700">{models.length}</div>
                <div className="text-sm text-gray-500">AI Models</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Verdict / Summary */}
      {detectionStatsByDetector.length > 0 && (() => {
        const sorted = [...detectionStatsByDetector].sort((a, b) => b.successRate - a.successRate);
        const bestDetector = sorted[0];
        const worstDetector = sorted[sorted.length - 1];
        const rateComment = avgDetectionRate !== null
          ? avgDetectionRate >= 80
            ? 'highly detectable'
            : avgDetectionRate >= 50
              ? 'moderately detectable'
              : 'difficult to detect'
          : null;

        return (
          <section className="section bg-white py-8">
            <div className="container">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Info className="w-6 h-6 text-blue-500" />
                  Verdict
                </h2>
                <div className="bg-blue-50 rounded-xl p-6">
                  <p className="text-gray-700 leading-relaxed">
                    Images generated by {service.name} are{' '}
                    {rateComment} with an average detection rate of{' '}
                    <strong>{avgDetectionRate}%</strong> across{' '}
                    <strong>{detectionStatsByDetector.length}</strong> tested detectors.
                    {detectionStatsByDetector.length > 1 && (
                      <>
                        {' '}<Link to={`/detectors/${bestDetector.slug}`} className="text-blue-600 hover:underline font-medium">
                          {bestDetector.name}
                        </Link>{' '}
                        is the most effective at catching {service.name} content ({bestDetector.successRate}%),
                        while{' '}
                        <Link to={`/detectors/${worstDetector.slug}`} className="text-blue-600 hover:underline font-medium">
                          {worstDetector.name}
                        </Link>{' '}
                        has the lowest detection rate ({worstDetector.successRate}%).
                      </>
                    )}
                    {models.length > 0 && (
                      <> {service.name} uses{' '}
                        <strong>{models.length}</strong> AI model{models.length > 1 ? 's' : ''} for content generation.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Service Info Cards */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              About {service.name}
            </h2>

            <div className="bg-white rounded-xl p-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Zap className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-medium text-gray-900 mb-1">Category</h3>
                  <p className="text-gray-600 text-sm">{getCategoryLabel(service.category)}</p>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-medium text-gray-900 mb-1">Pricing</h3>
                  <p className="text-gray-600 text-sm">
                    {service.hasFreeplan ? 'Free tier available' : 'Paid only'}
                  </p>
                  {service.pricingInfo && (
                    <p className="text-gray-500 text-xs mt-1">{service.pricingInfo}</p>
                  )}
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Shield className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-medium text-gray-900 mb-1">Watermarks</h3>
                  <p className="text-gray-600 text-sm">
                    {watermarks.length > 0
                      ? watermarks.map(w => w.name).join(', ')
                      : 'No watermarks detected'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detection Stats by Detector */}
      {detectionStatsByDetector.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-blue-500" />
                Detection Benchmark Results
              </h2>
              <p className="text-gray-500 mb-6">
                How well do AI detectors identify {service.name} generated images?
              </p>

              <div className="space-y-3">
                {detectionStatsByDetector.map((stat) => (
                  <Link
                    key={stat.slug}
                    to={`/detectors/${stat.slug}`}
                    className="flex items-center justify-between bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
                  >
                    <span className="font-medium text-gray-900">{stat.name}</span>
                    <div className="flex items-center gap-4">
                      {stat.avgScore !== null && (
                        <span className="text-sm text-gray-500">
                          Avg: {stat.avgScore}%
                        </span>
                      )}
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            stat.successRate >= 70 ? 'bg-green-500' :
                            stat.successRate >= 40 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${stat.successRate}%` }}
                        />
                      </div>
                      <span className="text-sm w-12 text-right">
                        {stat.successRate}%
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Watermarks */}
      {watermarks.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Droplet className="w-6 h-6 text-purple-500" />
                Watermarks Used by {service.name}
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                {watermarks.map((wm) => (
                  <Link
                    key={wm.slug}
                    to={`/watermarks/${wm.slug}`}
                    className="bg-white rounded-xl p-4 hover:shadow-md transition-shadow flex items-center gap-4"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{wm.name}</h3>
                      <p className="text-sm text-gray-500">{wm.developer}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      wm.isRemovable ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {wm.isRemovable ? 'Removable' : 'Partial'}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* AI Models */}
      {models.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Cpu className="w-6 h-6 text-purple-500" />
                AI Models Powering {service.name}
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                {models.map((model) => (
                  <Link
                    key={model.slug}
                    to={`/models/${model.slug}`}
                    className="bg-gray-50 rounded-xl p-4 hover:shadow-md transition-shadow flex items-center gap-4"
                  >
                    {model.logoUrl ? (
                      <img src={model.logoUrl} alt={model.name} className="w-10 h-10 object-contain" />
                    ) : (
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <Cpu className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{model.name}</h3>
                      <p className="text-sm text-gray-500">{model.vendor}</p>
                    </div>
                    {model.avgDetectionRate !== null && (
                      <span className={`text-sm font-medium ${
                        model.avgDetectionRate >= 70 ? 'text-red-600' :
                        model.avgDetectionRate >= 40 ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {model.avgDetectionRate}% detected
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Detection Stats by Model */}
      {detectionStatsByModel.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <FlaskConical className="w-6 h-6 text-purple-500" />
                Detection Rate by Model
              </h2>

              <div className="space-y-3">
                {detectionStatsByModel.map((stat) => (
                  <Link
                    key={stat.slug}
                    to={`/models/${stat.slug}`}
                    className="flex items-center justify-between bg-white rounded-xl p-4 hover:shadow-md transition-shadow"
                  >
                    <div>
                      <span className="font-medium text-gray-900">{stat.name}</span>
                      <span className="text-xs text-gray-500 ml-2">{stat.vendor}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            stat.successRate >= 70 ? 'bg-green-500' :
                            stat.successRate >= 40 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${stat.successRate}%` }}
                        />
                      </div>
                      <span className="text-sm w-12 text-right">
                        {stat.successRate}%
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Generators */}
      {generators.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {service.name} Tools
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                {generators.map((gen) => (
                  <div
                    key={gen.id}
                    className="bg-gray-50 rounded-xl p-4"
                  >
                    <h3 className="font-medium text-gray-900 mb-2">{gen.name}</h3>
                    {gen.description && (
                      <p className="text-sm text-gray-600 mb-3">{gen.description}</p>
                    )}
                    <div className="flex gap-2">
                      {gen.inputType && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          Input: {gen.inputType}
                        </span>
                      )}
                      {gen.outputType && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                          Output: {gen.outputType}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Benchmark Methodology */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-purple-500" />
              Benchmark Methodology
            </h2>
            <div className="bg-white rounded-xl p-6 space-y-4">
              <p className="text-gray-700 leading-relaxed">
                {service.name} images were tested across{' '}
                <strong>{detectionStatsByDetector.length}</strong> AI detectors
                {models.length > 0 && (
                  <> using content from <strong>{models.length}</strong> AI model{models.length > 1 ? 's' : ''}</>
                )}
                {watermarks.length > 0 && (
                  <> with <strong>{watermarks.length}</strong> watermark type{watermarks.length > 1 ? 's' : ''}</>
                )}
                . The dataset includes both AI-generated images and real photographs from verified sources.
              </p>
              <p className="text-gray-700 leading-relaxed">
                All detectors are tested under identical conditions using the same images.
                We record each detector's classification and confidence score,
                then compute detection rates — the percentage of {service.name} images
                correctly identified as AI-generated.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-700 font-medium text-sm"
              >
                See full benchmark methodology
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Text */}
      {service.longDescription && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-3xl mx-auto prose prose-gray">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {service.longDescription}
              </ReactMarkdown>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {service.faq && service.faq.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                {service.faq.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-5 text-left"
                    >
                      <span className="font-medium text-gray-900">{item.q}</span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-5 text-gray-600">
                        {item.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Related Services */}
      {relatedServices.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Compare With Other Services
              </h2>

              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {relatedServices.map((rs) => (
                  <Link
                    key={rs.slug}
                    to={`/services/${rs.slug}`}
                    className="bg-gray-50 rounded-xl p-4 hover:shadow-md transition-shadow flex items-start gap-3"
                  >
                    {rs.logoUrl && (
                      <img src={rs.logoUrl} alt={rs.name} className="w-8 h-8 object-contain flex-shrink-0" />
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">{rs.name}</h3>
                      <p className="text-sm text-gray-500 mb-2">{rs.vendor}</p>
                      <div className="flex items-center gap-2">
                        {rs.hasFreeplan && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            Free tier
                          </span>
                        )}
                        {rs.category && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                            {getCategoryLabel(rs.category)}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section bg-primary-600">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Test {service.name} Images in the Arena
          </h2>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Upload a {service.name} image and see how detectors perform in real time.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/arena"
              className={buttonStyles({
                variant: 'secondary',
                size: 'lg',
                className: 'bg-white text-primary-600 hover:bg-gray-100',
              })}
            >
              Start a Battle
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/"
              className={buttonStyles({
                variant: 'secondary',
                size: 'lg',
                className: 'bg-primary-500 text-white hover:bg-primary-400 border-primary-400',
              })}
            >
              View Leaderboard
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

export default ServicePage;
